<script setup>
	import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
	import { DsText, DsGrid, dsUseIsMobile } from '@coloplast/design-system';
	import ProductHighlightsHotspotDesktop from 'shared/components/ProductHighlights/ProductHighlightsHotspotDesktop.vue';
	import ProductHighlightsHotspotMobile from 'shared/components/ProductHighlights/ProductHighlightsHotspotMobile.vue';
	import ProductHighlightsCarousel from 'shared/components/ProductHighlights/ProductHighlightsCarousel.vue';
	import ProductHighlightsVideo from 'shared/components/ProductHighlights/ProductHighlightsVideo.vue';
	import InfiniteScroll from 'shared/components/InfiniteScroll.vue';
	import { useStore } from 'vuex';

	const store = useStore();

	const setActiveHighlight = (payload) => store.commit('setActiveHighlight', payload);

	const isFirstInteraction = computed(() => store.getters['getIsFirstInteraction']);
	const setIsFirstInteraction = (payload) => store.commit('setIsFirstInteraction', payload);

	const props = defineProps({
		vm: {
			type: Object,
			default: () => {},
		},
	});

	const { isMobile } = dsUseIsMobile();

	const imageRefMobile = ref(null);
	const imageRefDesktop = ref(null);
	const imageWidth = ref(0);
	const imageHeight = ref(0);
	const container = ref(null); // DOM element

	const getImageSize = () => {
		if (isMobile?.value && imageRefMobile?.value) {
			imageWidth.value = props.vm.CurrentBlock?.MobileImageWidth || imageRefMobile.value?.naturalWidth;
			imageHeight.value = props.vm.CurrentBlock?.MobileImageHeight || imageRefMobile.value?.naturalHeight;
		} else {
			imageWidth.value = props.vm.CurrentBlock?.DesktopImageWidth || imageRefDesktop.value?.naturalWidth;
			imageHeight.value = props.vm.CurrentBlock?.DesktopImageHeight || imageRefDesktop.value?.naturalHeight;
		}
	};

	const intersectionObserverCallback = (entries, observer) => {
		entries.forEach(entry => {
			if (entry.isIntersecting) {
				container.value.classList.add('-in-view');
				observer.disconnect();
			}
		});
	};

	onMounted(() => {
		getImageSize();
		window.addEventListener('resize', getImageSize);

		// hate having to do this, but if we don't use nextTick then these highlight might be activated prematurely as this component will not be very tall at this moment
		// since the child components haven't been rendered yet.
		nextTick(() => {
			let observer = new IntersectionObserver(intersectionObserverCallback, {
				threshold: 0.6,
			});
			observer.observe(container.value);
		})
	});

	onBeforeUnmount(() => {
		window.removeEventListener('resize', getImageSize);
	});

	const formatCoordinate = (coordinate, axis) => {
		if (axis === 'x') {
			return (coordinate / imageWidth.value) * 100;
		} else {
			return (coordinate / imageHeight.value) * 100;
		}
	};

	const showHighlight = () => {
		setTimeout(() => {
			if (!isFirstInteraction.value) {
				setActiveHighlight(1);
				setIsFirstInteraction(true);
			}
		}, 1000);
	};

	watch(
		() => isMobile?.value,
		(newValue, oldValue) => {
			if (oldValue === true && newValue === false) {
				setActiveHighlight(1);
			}
		}
	);

	const showVideo = ref(false);
	const videoPlayed = ref(false);

	const endVideo = () => {
		showVideo.value = false;
		showHighlight();
	};

	const triggerAction = () => {
		if (props.vm.BaseViewModel.VideoUrl && !videoPlayed.value) {
			showVideo.value = true;
			videoPlayed.value = true;
		}

		if (!props.vm.BaseViewModel.VideoUrl || (props.vm.BaseViewModel.VideoUrl && !showVideo.value && videoPlayed.value)) {
			showHighlight();
		}
	};

	const isMobileSetupReady = computed(() => {
		return !!(props.vm.BaseViewModel.MobileImageUrl &&
			props.vm.BaseViewModel.ProductHighlightItems.some(item => item?.XCoordMobile > 0) &&
			props.vm.BaseViewModel.ProductHighlightItems.some(item => item?.YCoordMobile > 0) &&
			!props.vm.BaseViewModel.VideoUrl);
	});

	const isDesktopSetupReady = computed(() => {
		return !!(props.vm.BaseViewModel.DesktopImageUrl &&
			props.vm.BaseViewModel.ProductHighlightItems.some(item => item?.XCoord > 0) &&
			props.vm.BaseViewModel.ProductHighlightItems.some(item => item?.YCoord > 0) &&
			!props.vm.BaseViewModel.VideoUrl);
	});

	const isVideoSetupReady = computed(() => {
		return !!(props.vm.BaseViewModel.DesktopImageUrl &&
			props.vm.BaseViewModel.ProductHighlightItems.some(item => item?.XCoord > 0) &&
			props.vm.BaseViewModel.ProductHighlightItems.some(item => item?.YCoord > 0) &&
			props.vm.BaseViewModel.VideoUrl);
	});

	const onSelectHotspot = () => {
		const menuHeight = document.querySelector('[data-main-navigation]').clientHeight;
		window.scrollBy(0, container.value.getBoundingClientRect().y - menuHeight);
	};
</script>

<template>
	<InfiniteScroll
		is-first-page-loaded
		@trigger="triggerAction"
	>
		<div class="c-product-highlights">
			<div
				v-if="vm.CurrentBlock.Headline || vm.CurrentBlock.Description"
				class="c-product-highlights__container"
			>
				<div class="c-product-highlights__intro">
					<DsGrid
						:columns="1"
						:columns-md="2"
						gap="md"
					>
						<DsText
							v-if="vm.CurrentBlock.Headline"
							html-element="h2"
							text-type="heading-3xl"
							class="c-product-highlights__headline"
							v-text="vm.CurrentBlock.Headline"
						>
						</DsText>
						<DsText
							v-if="vm.CurrentBlock.Description"
							class="c-product-highlights__description"
							v-html="vm.CurrentBlock.Description"
						/>
					</DsGrid>
				</div>
			</div>
			<div
				v-if="isMobileSetupReady || isDesktopSetupReady || isVideoSetupReady"
				class="c-product-highlights__background"
			>
				<div class="c-product-highlights__container" ref="container">
					<div class="c-product-highlights__wrapper">
						<ProductHighlightsVideo
							v-if="isVideoSetupReady && showVideo"
							:video-url="vm.BaseViewModel.VideoUrl"
							:show-video="showVideo"
							:width="imageWidth"
							:height="imageHeight"
							:title="vm.CurrentBlock.Headline"
							@video-ended="endVideo"
						/>
						<div v-show="!isVideoSetupReady || (isVideoSetupReady && !showVideo && videoPlayed)">
							<template v-if="isMobileSetupReady || isDesktopSetupReady || isVideoSetupReady">
								<img
									v-if="isMobile && isMobileSetupReady"
									ref="imageRefMobile"
									:src="vm.BaseViewModel.MobileImageUrl"
									:alt="vm.CurrentBlock.ImageAltText || vm.CurrentBlock.Headline"
									class="c-product-highlights__image"
									@load="getImageSize"
								/>
								<img
									v-if="(isMobile && !isMobileSetupReady && (isDesktopSetupReady || isVideoSetupReady)) || (!isMobile && (isDesktopSetupReady || isVideoSetupReady))"
									ref="imageRefDesktop"
									:src="vm.BaseViewModel.DesktopImageUrl"
									:alt="vm.CurrentBlock.ImageAltText || vm.CurrentBlock.Headline"
									class="c-product-highlights__image"
									:class="{ 'c-product-highlights__image--after-video' : vm.BaseViewModel.VideoUrl }"
									@load="getImageSize"
								/>
								<template v-if="isMobile && (isMobileSetupReady || isDesktopSetupReady || isVideoSetupReady)">
									<ProductHighlightsHotspotMobile
										v-for="(highlightMobile, index) in vm.BaseViewModel.ProductHighlightItems"
										:key="`${highlightMobile.Headline}-${index + 1}`"
										:item="highlightMobile"
										:ordinal-number="index + 1"
										@select-hotspot="onSelectHotspot"
										:style="{
											top: formatCoordinate(isMobileSetupReady ? highlightMobile.YCoordMobile : highlightMobile.YCoord, 'y') + '%',
											left: formatCoordinate(isMobileSetupReady ? highlightMobile.XCoordMobile : highlightMobile.XCoord, 'x') + '%',
										}"
									/>
								</template>
								<template v-if="!isMobile && (isDesktopSetupReady || isVideoSetupReady)">
									<ProductHighlightsHotspotDesktop
										v-for="(highlight, index) in vm.BaseViewModel.ProductHighlightItems"
										:key="`${highlight.Headline}-${index + 1}`"
										:item="highlight"
										:ordinal-number="index + 1"
										:style="{
											top: formatCoordinate(highlight.YCoord, 'y') + '%',
											left: formatCoordinate(highlight.XCoord, 'x') + '%',
										}"
									/>
								</template>
							</template>
						</div>
					</div>
				</div>

				<template v-if="isMobile && (isMobileSetupReady || isDesktopSetupReady || isVideoSetupReady)">
					<ProductHighlightsCarousel :items="vm.BaseViewModel.ProductHighlightItems"/>
				</template>
			</div>
		</div>
	</InfiniteScroll>
</template>
